import React, { useState } from 'react';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { storage } from '../../firebase/firebaseConfig';
import { compressImage } from '../../utils/imageUtils';
import { PACKAGE_TYPES, getPackageFeatures, isFeatureEnabled, getFeatureLimit, isUnlimited } from '../../data/packageModels';
import { validatePromptPayNumber } from '../../utils/qrCodeUtils';

const PaymentMethodsForm = ({ payment, packageType = PACKAGE_TYPES.FREE, onChange }) => {
  const [loading, setLoading] = useState(false);
  const [qrCodeValidationErrors, setQrCodeValidationErrors] = useState({});
  
  // Get package features
  const features = getPackageFeatures(packageType);
  const maxBankAccounts = getFeatureLimit(packageType, 'maxBankAccounts');
  const qrCodeEnabled = isFeatureEnabled(packageType, 'qrCodeEnabled');
  const qrCodeGenerateEnabled = isFeatureEnabled(packageType, 'qrCodeGenerate');
  const maxQRCodeGenerate = getFeatureLimit(packageType, 'maxQRCodeGenerate');
  const codEnabled = isFeatureEnabled(packageType, 'cashOnDelivery');
  const adminMiddlemanEnabled = isFeatureEnabled(packageType, 'adminMiddleman');
  
  const paymentData = payment || {
    methods: ['bank_transfer'],
    bankAccounts: [],
    qrCodes: [],
    cashOnPickup: { enabled: false, location: '', time: '' },
    cashOnDelivery: { enabled: false, courierCompanies: [] }
  };

  const handleAddBankAccount = () => {
    const currentCount = paymentData.bankAccounts?.length || 0;
    if (!isUnlimited(maxBankAccounts) && currentCount >= maxBankAccounts) {
      alert(`แพ็กเกจปัจจุบันรองรับได้สูงสุด ${maxBankAccounts} บัญชี กรุณาอัพเกรด Package`);
      return;
    }
    
    const newAccount = { bank: '', accountNumber: '', accountName: '' };
    onChange({
      ...paymentData,
      bankAccounts: [...(paymentData.bankAccounts || []), newAccount]
    });
  };

  const handleRemoveBankAccount = (index) => {
    const newAccounts = paymentData.bankAccounts.filter((_, i) => i !== index);
    onChange({
      ...paymentData,
      bankAccounts: newAccounts
    });
  };

  const handleUpdateBankAccount = (index, field, value) => {
    const newAccounts = [...paymentData.bankAccounts];
    newAccounts[index] = { ...newAccounts[index], [field]: value };
    onChange({
      ...paymentData,
      bankAccounts: newAccounts
    });
  };

  const handleAddQRCode = async (type = 'upload') => {
    if (!qrCodeEnabled && type === 'upload') {
      alert('QR Code PromptPay (อัพโหลดรูป) ใช้ได้เฉพาะร้านที่สมัคร Package (Basic, Premium, Enterprise)');
      return;
    }
    
    if (!qrCodeGenerateEnabled && type === 'generate') {
      alert('QR Code Generate (แสดงยอดเงินอัตโนมัติ) ใช้ได้เฉพาะร้านที่สมัคร Package Premium หรือ Enterprise');
      return;
    }
    
    // ตรวจสอบจำนวน QR Code Generate ที่มีอยู่
    if (type === 'generate') {
      const currentGenerateCount = (paymentData.qrCodes || []).filter(qr => qr.type === 'generate').length;
      
      if (!isUnlimited(maxQRCodeGenerate) && currentGenerateCount >= maxQRCodeGenerate) {
        if (maxQRCodeGenerate === 3) {
          alert(`แพ็กเกจ Premium จำกัด ${maxQRCodeGenerate} QR Codes Generate\nกรุณาอัพเกรดเป็น Enterprise เพื่อใช้ไม่จำกัด`);
        } else {
          alert(`แพ็กเกจปัจจุบันรองรับได้สูงสุด ${maxQRCodeGenerate} QR Codes Generate กรุณาอัพเกรด Package`);
        }
        return;
      }
    }
    
    const newQRCode = type === 'generate' 
      ? { type: 'generate', promptPayNumber: '', amount: '', description: '' }
      : { type: 'upload', qrCode: '', amount: '', description: '' };
    
    onChange({
      ...paymentData,
      qrCodes: [...(paymentData.qrCodes || []), newQRCode]
    });
  };

  const handleRemoveQRCode = (index) => {
    const newQRCodes = paymentData.qrCodes.filter((_, i) => i !== index);
    onChange({
      ...paymentData,
      qrCodes: newQRCodes
    });
  };

  const handleUpdateQRCode = (index, field, value) => {
    const newQRCodes = [...paymentData.qrCodes];
    const currentQR = newQRCodes[index] || {};
    
    // ถ้าเปลี่ยน type ให้ reset fields ที่ไม่เกี่ยวข้อง
    if (field === 'type') {
      if (value === 'generate') {
        newQRCodes[index] = { 
          type: 'generate', 
          promptPayNumber: '', 
          description: currentQR.description || '' 
        };
        // Clear validation error when switching to generate
        const newErrors = { ...qrCodeValidationErrors };
        delete newErrors[index];
        setQrCodeValidationErrors(newErrors);
      } else {
        newQRCodes[index] = { 
          type: 'upload', 
          qrCode: currentQR.qrCode || '', 
          amount: currentQR.amount || '', 
          description: currentQR.description || '' 
        };
        // Clear validation error when switching to upload
        const newErrors = { ...qrCodeValidationErrors };
        delete newErrors[index];
        setQrCodeValidationErrors(newErrors);
      }
    } else {
      newQRCodes[index] = { ...currentQR, [field]: value };
      
      // Validate PromptPay number when it's updated
      if (field === 'promptPayNumber' && newQRCodes[index].type === 'generate') {
        const validation = validatePromptPayNumber(value);
        const newErrors = { ...qrCodeValidationErrors };
        if (!validation.isValid) {
          newErrors[index] = validation.error;
        } else {
          delete newErrors[index];
        }
        setQrCodeValidationErrors(newErrors);
      }
    }
    
    onChange({
      ...paymentData,
      qrCodes: newQRCodes
    });
  };

  const handleUploadQRCode = async (file, index) => {
    try {
      setLoading(true);
      
      if (!file.type.startsWith('image/')) {
        throw new Error('ไฟล์ต้องเป็นรูปภาพ');
      }
      if (file.size > 5 * 1024 * 1024) {
        throw new Error('ไฟล์มีขนาดใหญ่เกินไป (ไม่เกิน 5MB)');
      }
      
      let fileToUpload = file;
      try {
        fileToUpload = await compressImage(file, 400, 400, 0.9);
      } catch (compressError) {
        console.warn('Error compressing image, using original:', compressError);
        fileToUpload = file;
      }
      
      const timestamp = Date.now();
      const randomString = Math.random().toString(36).substring(2, 15);
      const fileName = `stores/qrCode_${timestamp}_${randomString}.jpg`;
      const storageRef = ref(storage, fileName);
      
      await uploadBytes(storageRef, fileToUpload);
      const downloadURL = await getDownloadURL(storageRef);
      
      handleUpdateQRCode(index, 'qrCode', downloadURL);
      alert('อัพโหลด QR Code สำเร็จ!');
    } catch (error) {
      console.error('Error uploading QR code:', error);
      alert('เกิดข้อผิดพลาดในการอัพโหลด: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const togglePaymentMethod = (method) => {
    const methods = paymentData.methods || [];
    if (methods.includes(method)) {
      onChange({
        ...paymentData,
        methods: methods.filter(m => m !== method)
      });
    } else {
      onChange({
        ...paymentData,
        methods: [...methods, method]
      });
    }
  };

  const banks = [
    'กรุงเทพ', 'กรุงไทย', 'กสิกรไทย', 'ไทยพาณิชย์', 
    'ทหารไทยธนชาต', 'ยูโอบี', 'ซีไอเอ็มบี', 'ออมสิน', 'อื่นๆ'
  ];

  const courierCompanies = ['J&T', 'Flash', 'Kerry', 'DHL', 'FedEx', 'Nim Express'];

  return (
    <div className="space-y-6">
      <h3 className="text-lg font-semibold text-gray-700 border-b pb-2">ข้อมูลการชำระเงิน</h3>
      
      {/* วิธีการชำระเงินที่รองรับ */}
      <div>
        <label className="block text-sm font-semibold text-gray-700 mb-3">
          วิธีการชำระเงินที่รองรับ
        </label>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <label className="flex items-center gap-2 p-3 border-2 rounded-lg cursor-pointer hover:bg-gray-50 transition-colors">
            <input
              type="checkbox"
              checked={paymentData.methods?.includes('bank_transfer')}
              onChange={() => togglePaymentMethod('bank_transfer')}
              className="w-4 h-4 text-green-500"
            />
            <span className="text-sm">🏦 โอนบัญชี</span>
          </label>
          
          <label className={`flex items-center gap-2 p-3 border-2 rounded-lg cursor-pointer hover:bg-gray-50 transition-colors ${
            !qrCodeEnabled ? 'opacity-50 cursor-not-allowed' : ''
          }`}>
            <input
              type="checkbox"
              checked={paymentData.methods?.includes('qr_code')}
              onChange={() => {
                if (qrCodeEnabled) {
                  togglePaymentMethod('qr_code');
                } else {
                  alert('QR Code PromptPay ใช้ได้เฉพาะร้านที่สมัคร Package');
                }
              }}
              disabled={!qrCodeEnabled}
              className="w-4 h-4 text-green-500"
            />
            <span className="text-sm">📱 QR Code</span>
            {!qrCodeEnabled && <span className="text-xs text-red-500">(ต้องสมัคร Package)</span>}
          </label>
          
          <label className="flex items-center gap-2 p-3 border-2 rounded-lg cursor-pointer hover:bg-gray-50 transition-colors">
            <input
              type="checkbox"
              checked={paymentData.methods?.includes('cash_on_pickup')}
              onChange={() => togglePaymentMethod('cash_on_pickup')}
              className="w-4 h-4 text-green-500"
            />
            <span className="text-sm">💰 จ่ายสด นัดรับ</span>
          </label>
          
          <label className={`flex items-center gap-2 p-3 border-2 rounded-lg cursor-pointer hover:bg-gray-50 transition-colors ${
            !codEnabled ? 'opacity-50 cursor-not-allowed' : ''
          }`}>
            <input
              type="checkbox"
              checked={paymentData.methods?.includes('cash_on_delivery')}
              onChange={() => {
                if (codEnabled) {
                  togglePaymentMethod('cash_on_delivery');
                } else {
                  alert('เก็บปลายทาง ใช้ได้เฉพาะร้านที่สมัคร Package Premium หรือ Enterprise');
                }
              }}
              disabled={!codEnabled}
              className="w-4 h-4 text-green-500"
            />
            <span className="text-sm">📦 เก็บปลายทาง</span>
            {!codEnabled && <span className="text-xs text-red-500">(Premium+)</span>}
          </label>
        </div>
      </div>

      {/* บัญชีธนาคาร */}
      {paymentData.methods?.includes('bank_transfer') && (
        <div className="border border-gray-200 rounded-lg p-4">
          <div className="flex items-center justify-between mb-4">
            <h4 className="text-sm font-semibold text-gray-700">🏦 บัญชีธนาคาร</h4>
            <div className="flex items-center gap-2">
              <span className="text-xs text-gray-500">
                {paymentData.bankAccounts?.length || 0} / {isUnlimited(maxBankAccounts) ? '∞' : maxBankAccounts}
              </span>
              <button
                type="button"
                onClick={handleAddBankAccount}
                className="px-3 py-1 text-sm bg-green-500 text-white rounded-lg hover:bg-green-600 transition-colors"
              >
                + เพิ่มบัญชี
              </button>
            </div>
          </div>
          
          {paymentData.bankAccounts && paymentData.bankAccounts.length > 0 ? (
            <div className="space-y-4">
              {paymentData.bankAccounts.map((account, index) => (
                <div key={index} className="p-4 bg-gray-50 rounded-lg border border-gray-200">
                  <div className="flex items-center justify-between mb-3">
                    <span className="text-sm font-medium text-gray-700">บัญชี #{index + 1}</span>
                    {paymentData.bankAccounts.length > 1 && (
                      <button
                        type="button"
                        onClick={() => handleRemoveBankAccount(index)}
                        className="text-red-500 hover:text-red-700 text-sm"
                      >
                        ลบ
                      </button>
                    )}
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                    <div>
                      <label className="block text-xs text-gray-600 mb-1">ธนาคาร</label>
                      <select
                        value={account.bank || ''}
                        onChange={(e) => handleUpdateBankAccount(index, 'bank', e.target.value)}
                        className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
                      >
                        <option value="">เลือกธนาคาร</option>
                        {banks.map(bank => (
                          <option key={bank} value={bank}>{bank}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="block text-xs text-gray-600 mb-1">เลขบัญชี</label>
                      <input
                        type="text"
                        value={account.accountNumber || ''}
                        onChange={(e) => handleUpdateBankAccount(index, 'accountNumber', e.target.value)}
                        className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
                        placeholder="123-4-56789-0"
                      />
                    </div>
                    <div>
                      <label className="block text-xs text-gray-600 mb-1">ชื่อบัญชี</label>
                      <input
                        type="text"
                        value={account.accountName || ''}
                        onChange={(e) => handleUpdateBankAccount(index, 'accountName', e.target.value)}
                        className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
                        placeholder="ชื่อบัญชี"
                      />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-4 text-gray-500 text-sm">
              ยังไม่มีบัญชีธนาคาร คลิก "+ เพิ่มบัญชี" เพื่อเพิ่ม
            </div>
          )}
        </div>
      )}

      {/* QR Code PromptPay */}
      {paymentData.methods?.includes('qr_code') && (qrCodeEnabled || qrCodeGenerateEnabled) && (
        <div className="border border-gray-200 rounded-lg p-4">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h4 className="text-sm font-semibold text-gray-700">📱 QR Code PromptPay</h4>
              {qrCodeGenerateEnabled && (
                <span className="text-xs text-gray-500">
                  Generate: {(paymentData.qrCodes || []).filter(qr => qr.type === 'generate').length} / {isUnlimited(maxQRCodeGenerate) ? '∞' : maxQRCodeGenerate}
                </span>
              )}
            </div>
            <div className="flex gap-2">
              {qrCodeEnabled && (
                <button
                  type="button"
                  onClick={() => handleAddQRCode('upload')}
                  className="px-3 py-1 text-xs bg-green-500 text-white rounded-lg hover:bg-green-600 transition-colors"
                >
                  + อัพโหลด QR Code
                </button>
              )}
              {qrCodeGenerateEnabled && (
                <button
                  type="button"
                  onClick={() => handleAddQRCode('generate')}
                  className="px-3 py-1 text-xs bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors"
                >
                  + Generate QR Code
                </button>
              )}
            </div>
          </div>
          
          {paymentData.qrCodes && paymentData.qrCodes.length > 0 ? (
            <div className="space-y-4">
              {paymentData.qrCodes.map((qr, index) => {
                const qrType = qr.type || 'upload'; // default เป็น upload
                return (
                  <div key={index} className="p-4 bg-gray-50 rounded-lg border border-gray-200">
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-medium text-gray-700">QR Code #{index + 1}</span>
                        {qrType === 'generate' && (
                          <span className="text-xs px-2 py-0.5 bg-blue-100 text-blue-700 rounded">Generate</span>
                        )}
                        {qrType === 'upload' && (
                          <span className="text-xs px-2 py-0.5 bg-green-100 text-green-700 rounded">Upload</span>
                        )}
                      </div>
                      <div className="flex items-center gap-2">
                        {/* Toggle Type (ถ้า package รองรับทั้ง 2 วิธี) */}
                        {qrCodeEnabled && qrCodeGenerateEnabled && (
                          <select
                            value={qrType}
                            onChange={(e) => handleUpdateQRCode(index, 'type', e.target.value)}
                            className="text-xs px-2 py-1 border border-gray-300 rounded"
                          >
                            <option value="upload">อัพโหลด</option>
                            <option value="generate">Generate</option>
                          </select>
                        )}
                        <button
                          type="button"
                          onClick={() => handleRemoveQRCode(index)}
                          className="text-red-500 hover:text-red-700 text-sm"
                        >
                          ลบ
                        </button>
                      </div>
                    </div>
                    
                    {qrType === 'upload' ? (
                      /* Upload QR Code */
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-xs text-gray-600 mb-2">QR Code Image</label>
                          <div className="flex items-center gap-3">
                            {qr.qrCode && (
                              <img
                                src={qr.qrCode}
                                alt={`QR Code ${index + 1}`}
                                className="w-24 h-24 object-contain border border-gray-300 rounded-lg"
                              />
                            )}
                            <div className="flex-1">
                              <input
                                type="file"
                                accept="image/*"
                                onChange={(e) => {
                                  const file = e.target.files[0];
                                  if (file) handleUploadQRCode(file, index);
                                }}
                                className="w-full text-xs text-gray-600 file:mr-2 file:py-1 file:px-2 file:rounded file:border-0 file:text-xs file:font-semibold file:bg-green-50 file:text-green-700"
                                disabled={loading}
                              />
                            </div>
                          </div>
                        </div>
                        <div className="space-y-3">
                          <div>
                            <label className="block text-xs text-gray-600 mb-1">ยอดเงิน (บาท)</label>
                            <input
                              type="number"
                              value={qr.amount || ''}
                              onChange={(e) => handleUpdateQRCode(index, 'amount', e.target.value)}
                              min="0"
                              step="0.01"
                              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
                              placeholder="0.00"
                            />
                            <p className="text-xs text-gray-500 mt-1">ถ้าไม่ระบุจะแสดงยอดเงินตามราคาสินค้า</p>
                          </div>
                          <div>
                            <label className="block text-xs text-gray-600 mb-1">คำอธิบาย (ไม่บังคับ)</label>
                            <input
                              type="text"
                              value={qr.description || ''}
                              onChange={(e) => handleUpdateQRCode(index, 'description', e.target.value)}
                              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
                              placeholder="เช่น QR Code สำหรับชำระเงินสด"
                            />
                          </div>
                        </div>
                      </div>
                    ) : (
                      /* Generate QR Code */
                      <div className="space-y-3">
                        <div>
                          <label className="block text-xs text-gray-600 mb-1">PromptPay Number *</label>
                          <input
                            type="text"
                            value={qr.promptPayNumber || ''}
                            onChange={(e) => handleUpdateQRCode(index, 'promptPayNumber', e.target.value)}
                            className={`w-full px-3 py-2 text-sm border rounded-lg focus:outline-none focus:ring-2 ${
                              qrCodeValidationErrors[index]
                                ? 'border-red-300 focus:ring-red-500'
                                : 'border-gray-300 focus:ring-blue-500'
                            }`}
                            placeholder="เบอร์โทรศัพท์ (10 หลัก) หรือ เลขบัตรประชาชน (13 หลัก)"
                            required
                          />
                          {qrCodeValidationErrors[index] ? (
                            <p className="text-xs text-red-500 mt-1">{qrCodeValidationErrors[index]}</p>
                          ) : (
                            <p className="text-xs text-gray-500 mt-1">ระบบจะ Generate QR Code พร้อมแสดงยอดเงินอัตโนมัติ</p>
                          )}
                        </div>
                        <div>
                          <label className="block text-xs text-gray-600 mb-1">คำอธิบาย (ไม่บังคับ)</label>
                          <input
                            type="text"
                            value={qr.description || ''}
                            onChange={(e) => handleUpdateQRCode(index, 'description', e.target.value)}
                            className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                            placeholder="เช่น QR Code สำหรับชำระเงินสด"
                          />
                        </div>
                        <div className="p-3 bg-blue-50 rounded-lg border border-blue-200">
                          <p className="text-xs text-blue-800">
                            <span className="font-semibold">✨ Generate QR Code:</span> ระบบจะสร้าง QR Code อัตโนมัติพร้อมแสดงยอดเงินเมื่อลูกค้าสั่งซื้อ
                          </p>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="text-center py-4 text-gray-500 text-sm">
              ยังไม่มี QR Code คลิก "+ เพิ่ม QR Code" เพื่อเพิ่ม
            </div>
          )}
        </div>
      )}

      {/* จ่ายสด นัดรับ */}
      {paymentData.methods?.includes('cash_on_pickup') && (
        <div className="border border-gray-200 rounded-lg p-4">
          <h4 className="text-sm font-semibold text-gray-700 mb-4">💰 จ่ายสด นัดรับ</h4>
          <div className="space-y-3">
            <div>
              <label className="block text-xs text-gray-600 mb-1">ที่อยู่สำหรับนัดรับ</label>
              <input
                type="text"
                value={paymentData.cashOnPickup?.location || ''}
                onChange={(e) => onChange({
                  ...paymentData,
                  cashOnPickup: { ...paymentData.cashOnPickup, location: e.target.value }
                })}
                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
                placeholder="ที่อยู่สำหรับนัดรับ"
              />
            </div>
            <div>
              <label className="block text-xs text-gray-600 mb-1">เวลา</label>
              <input
                type="text"
                value={paymentData.cashOnPickup?.time || ''}
                onChange={(e) => onChange({
                  ...paymentData,
                  cashOnPickup: { ...paymentData.cashOnPickup, time: e.target.value }
                })}
                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
                placeholder="เช่น 09:00 - 17:00"
              />
            </div>
          </div>
        </div>
      )}

      {/* เก็บปลายทาง */}
      {paymentData.methods?.includes('cash_on_delivery') && codEnabled && (
        <div className="border border-gray-200 rounded-lg p-4">
          <h4 className="text-sm font-semibold text-gray-700 mb-4">📦 เก็บปลายทาง (COD)</h4>
          <div>
            <label className="block text-xs text-gray-600 mb-2">บริษัทขนส่งที่รองรับ COD</label>
            <div className="flex flex-wrap gap-2">
              {courierCompanies.map(company => (
                <label key={company} className="flex items-center gap-2 px-3 py-2 border border-gray-300 rounded-lg cursor-pointer hover:bg-gray-50">
                  <input
                    type="checkbox"
                    checked={paymentData.cashOnDelivery?.courierCompanies?.includes(company)}
                    onChange={(e) => {
                      const companies = paymentData.cashOnDelivery?.courierCompanies || [];
                      const newCompanies = e.target.checked
                        ? [...companies, company]
                        : companies.filter(c => c !== company);
                      onChange({
                        ...paymentData,
                        cashOnDelivery: {
                          ...paymentData.cashOnDelivery,
                          courierCompanies: newCompanies
                        }
                      });
                    }}
                    className="w-4 h-4 text-green-500"
                  />
                  <span className="text-sm text-gray-700">{company}</span>
                </label>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Admin Middleman */}
      {adminMiddlemanEnabled && (
        <div className="border border-gray-200 rounded-lg p-4 bg-blue-50">
          <div className="flex items-start gap-3">
            <input
              type="checkbox"
              checked={paymentData.adminMiddleman?.enabled || false}
              onChange={(e) => {
                onChange({
                  ...paymentData,
                  adminMiddleman: {
                    ...paymentData.adminMiddleman,
                    enabled: e.target.checked,
                    feeRate: paymentData.adminMiddleman?.feeRate || 0.05
                  }
                });
              }}
              className="mt-1 w-4 h-4 text-green-500"
            />
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-2">
                <span className="text-sm font-semibold text-gray-700">🛡️ แอดมินเป็นตัวกลาง</span>
                <span className="text-xs text-blue-600 font-medium">(Premium+)</span>
              </div>
              <p className="text-xs text-gray-600 mb-3">
                เปิดใช้งานให้แอดมินเป็นตัวกลางในการชำระเงิน ค่าธรรมเนียม 5% ของยอดสั่งซื้อ (หักจากเหรียญ)
              </p>
              {paymentData.adminMiddleman?.enabled && (
                <div className="bg-white p-3 rounded-lg border border-blue-200">
                  <p className="text-xs text-gray-600 mb-1">
                    <span className="font-semibold">ค่าธรรมเนียม:</span> 5% ของยอดสั่งซื้อ
                  </p>
                  <p className="text-xs text-gray-600">
                    <span className="font-semibold">การหัก:</span> หักจากเหรียญในระบบ (ไม่ใช่เงิน)
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PaymentMethodsForm;

